import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
  Alert,
} from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import dayjs from 'dayjs';
import UserSelectbox from '../common/form/autocomplete/UserSelectbox';
import { LEAVE_TYPE_LABELS } from '../../containers/attendancemng/AttendMngContainer';

// bito 포팅(2026-10-19): ksm은 ApprovalV2 결재선 템플릿을 선택/구성하는 화면이었지만, bito는
// 그 엔진이 없어 결재자를 1명 직접 지정하는 단순 결재로 바꿨다(백엔드가 COM_APPR_DET를
// DOCY_TYP='HR_LEAVE'로 재사용). 결재자 여러 명 순차 결재가 필요해지면 UserSelectbox를
// 여러 개 추가하는 형태로 확장하면 된다(approverUuids는 배열로 받음).
//
// 신규 휴가 2종:
//  - MATERNITY(출산휴가): 날짜 범위만 입력, 연차 차감 없음(서버가 집계에서 제외).
//  - ANNUAL_HOURLY(1시간 단위 연차): 시작 시각 + 신청 시간(1시간 단위)을 입력하면 서버가
//    표준근무시간(기본 8h)으로 환산한 일수를 계산해 돌려준다. 여기서는 8h 가정으로 "예상
//    차감일수"만 미리 보여주고, 실제 차감은 서버 응답(및 /balance 재조회)이 기준이다.

const DEFAULT_DAYS = {
  HALF_AM: 0.5,
  HALF_PM: 0.5,
  EARLY_LEAVE: 0.5,
};

const SINGLE_DAY_TYPES = ['HALF_AM', 'HALF_PM', 'EARLY_LEAVE', 'ANNUAL_HOURLY'];

const buildValidationSchema = () =>
  yup.object({
    leaveTyp: yup.string().required('유형을 선택하세요'),
    leaveDtSt: yup.string().required('시작일을 입력하세요'),
    leaveDtEd: yup.string().required('종료일을 입력하세요'),
    leaveDays: yup.number().when('leaveTyp', {
      is: 'ANNUAL_HOURLY',
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.min(0.5).required('일수를 입력하세요'),
    }),
    leaveHours: yup.number().when('leaveTyp', {
      is: 'ANNUAL_HOURLY',
      then: (schema) => schema.min(1, '1시간 이상 입력하세요').integer('1시간 단위로 입력하세요').required('신청 시간을 입력하세요'),
      otherwise: (schema) => schema.notRequired(),
    }),
    leaveStartTm: yup.string().when('leaveTyp', {
      is: 'ANNUAL_HOURLY',
      then: (schema) => schema.required('시작 시각을 입력하세요'),
      otherwise: (schema) => schema.notRequired(),
    }),
    approverUuid: yup.string().required('결재자를 지정하세요'),
  });

const LeaveRequestModal = ({ open, onClose, onSave, balance }) => {
  const formik = useFormik({
    initialValues: {
      leaveTyp: 'ANNUAL',
      leaveDtSt: dayjs().format('YYYY-MM-DD'),
      leaveDtEd: dayjs().format('YYYY-MM-DD'),
      leaveDays: 1,
      leaveHours: 1,
      leaveStartTm: '09:00',
      rmk: '',
      approverUuid: '',
    },
    enableReinitialize: true,
    validationSchema: buildValidationSchema(),
    onSubmit: (values) => {
      const isHourly = values.leaveTyp === 'ANNUAL_HOURLY';
      onSave({
        leaveTyp: values.leaveTyp,
        leaveDtSt: values.leaveDtSt,
        leaveDtEd: isHourly ? values.leaveDtSt : values.leaveDtEd,
        leaveDays: isHourly ? undefined : Number(values.leaveDays),
        leaveHours: isHourly ? Number(values.leaveHours) : undefined,
        leaveStartTm: isHourly ? values.leaveStartTm : undefined,
        rmk: values.rmk,
        approverUuids: [values.approverUuid],
      });
    },
  });

  useEffect(() => {
    if (open) {
      formik.resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const isHourly = formik.values.leaveTyp === 'ANNUAL_HOURLY';
  const isSingleDay = SINGLE_DAY_TYPES.includes(formik.values.leaveTyp);
  const deductsAnnual = ['ANNUAL', 'HALF_AM', 'HALF_PM', 'ANNUAL_HOURLY'].includes(formik.values.leaveTyp);
  const estimatedDays = isHourly ? Number(formik.values.leaveHours || 0) / 8 : Number(formik.values.leaveDays || 0);

  const handleLeaveTypChange = (e) => {
    const leaveTyp = e.target.value;
    formik.setFieldValue('leaveTyp', leaveTyp);
    if (DEFAULT_DAYS[leaveTyp]) {
      formik.setFieldValue('leaveDays', DEFAULT_DAYS[leaveTyp]);
    }
    if (SINGLE_DAY_TYPES.includes(leaveTyp)) {
      formik.setFieldValue('leaveDtEd', formik.values.leaveDtSt);
    }
  };

  const handleDateChange = (field, value) => {
    formik.setFieldValue(field, value);
    if (isSingleDay) {
      formik.setFieldValue('leaveDtSt', value);
      formik.setFieldValue('leaveDtEd', value);
      return;
    }
    const st = field === 'leaveDtSt' ? value : formik.values.leaveDtSt;
    const ed = field === 'leaveDtEd' ? value : formik.values.leaveDtEd;
    if (st && ed && !DEFAULT_DAYS[formik.values.leaveTyp]) {
      const diff = dayjs(ed).diff(dayjs(st), 'day') + 1;
      formik.setFieldValue('leaveDays', Math.max(1, diff));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>휴가 신청</DialogTitle>
      <DialogContent dividers>
        {balance && (
          <Alert severity={deductsAnnual && estimatedDays > balance.balanceDays ? 'warning' : 'info'} sx={{ mb: 2 }}>
            내 연차 잔여일수: {balance.balanceDays}일 (부여 {balance.grantDays}일 / 사용 {balance.usedDays}일)
            {deductsAnnual && estimatedDays > balance.balanceDays && ' - 신청 일수가 잔여일수를 초과합니다.'}
          </Alert>
        )}
        <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              size="small"
              label="휴가 유형 *"
              name="leaveTyp"
              value={formik.values.leaveTyp}
              onChange={handleLeaveTypChange}
              error={formik.touched.leaveTyp && Boolean(formik.errors.leaveTyp)}
              helperText={formik.touched.leaveTyp && formik.errors.leaveTyp}
            >
              {Object.entries(LEAVE_TYPE_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="date"
              size="small"
              label="시작일 *"
              InputLabelProps={{ shrink: true }}
              name="leaveDtSt"
              value={formik.values.leaveDtSt}
              onChange={(e) => handleDateChange('leaveDtSt', e.target.value)}
              error={formik.touched.leaveDtSt && Boolean(formik.errors.leaveDtSt)}
              helperText={formik.touched.leaveDtSt && formik.errors.leaveDtSt}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="date"
              size="small"
              label="종료일 *"
              InputLabelProps={{ shrink: true }}
              name="leaveDtEd"
              value={formik.values.leaveDtEd}
              onChange={(e) => handleDateChange('leaveDtEd', e.target.value)}
              disabled={isSingleDay}
              error={formik.touched.leaveDtEd && Boolean(formik.errors.leaveDtEd)}
              helperText={isSingleDay ? '해당 유형은 단일 일자만 신청 가능합니다.' : (formik.touched.leaveDtEd && formik.errors.leaveDtEd)}
            />
          </Grid>

          {isHourly ? (
            <>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="time"
                  size="small"
                  label="시작 시각 *"
                  InputLabelProps={{ shrink: true }}
                  name="leaveStartTm"
                  value={formik.values.leaveStartTm}
                  onChange={formik.handleChange}
                  error={formik.touched.leaveStartTm && Boolean(formik.errors.leaveStartTm)}
                  helperText={formik.touched.leaveStartTm && formik.errors.leaveStartTm}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  size="small"
                  label="신청 시간(1시간 단위) *"
                  inputProps={{ min: 1, step: 1 }}
                  name="leaveHours"
                  value={formik.values.leaveHours}
                  onChange={formik.handleChange}
                  error={formik.touched.leaveHours && Boolean(formik.errors.leaveHours)}
                  helperText={
                    (formik.touched.leaveHours && formik.errors.leaveHours)
                    || `예상 차감일수: 약 ${estimatedDays.toFixed(3)}일 (표준 8시간 기준, 실제 값은 근무유형에 따라 서버가 계산)`
                  }
                />
              </Grid>
            </>
          ) : (
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                size="small"
                label="일수 *"
                inputProps={{ min: 0.5, step: 0.5 }}
                name="leaveDays"
                value={formik.values.leaveDays}
                onChange={formik.handleChange}
                error={formik.touched.leaveDays && Boolean(formik.errors.leaveDays)}
                helperText={formik.touched.leaveDays && formik.errors.leaveDays}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <UserSelectbox
              formik={formik}
              name="approverUuid"
              label="결재자 *"
              variant="outlined"
              margin="none"
              size="small"
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              size="small"
              label="사유"
              name="rmk"
              value={formik.values.rmk}
              onChange={formik.handleChange}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">취소</Button>
        <Button onClick={formik.handleSubmit} variant="contained">결재 상신</Button>
      </DialogActions>
    </Dialog>
  );
};

export default LeaveRequestModal;
